import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Toast, ToastBody, ToastHeader } from "reactstrap";
import type { Notification } from "../types/notification.types";
import {
  NOTIFICATION_CATEGORY_ICON,
  NOTIFICATION_DEFAULT_ICON,
  NOTIFICATION_SOURCE_ROUTES,
} from "../constants/notification.constants";
import {
  NotificationCategory,
  NotificationSourceType,
} from "../types/notification.types";

const ACTOR_AVATAR_CATEGORIES = new Set<string>([
  NotificationCategory.LEAVE_APPLICATION,
]);
const DISMISS_MS = 10000;

const CATEGORY_LABEL: Partial<Record<NotificationCategory, string>> = {
  [NotificationCategory.WORK_ASSIGNMENT]: "Work Assignment",
  [NotificationCategory.LEAVE_APPLICATION]: "Leave",
  [NotificationCategory.WORK_ORDER_BILLING]: "Billing",
  [NotificationCategory.PAYMENT_EXPECTATION_FOLLOW_UP]: "Payment",
  [NotificationCategory.PAYROLL]: "Payroll",
  [NotificationCategory.SECURITY]: "Security",
  [NotificationCategory.WORK_ASSIGNMENT_REMOVAL]: "Assignment",
  [NotificationCategory.AMC_READY_FOR_ACTIVATION]: "AMC",
  [NotificationCategory.AMC_ACTIVATION]: "AMC",
  [NotificationCategory.AMC_COMPLETION]: "AMC",
  [NotificationCategory.AMC_CANCELLATION]: "AMC",
};

function getIcon(category: string) {
  return (
    NOTIFICATION_CATEGORY_ICON[category as NotificationCategory] ??
    NOTIFICATION_DEFAULT_ICON
  );
}

function resolveRoute(n: Notification): string | null {
  if (!n.sourceType || !n.sourceId) return null;
  const base =
    NOTIFICATION_SOURCE_ROUTES[n.sourceType as NotificationSourceType];
  return base ? `${base}/${n.sourceId}` : null;
}

interface Props {
  notification: Notification;
  onDismiss: () => void;
  persistent?: boolean;
}

const NotificationPreviewToast = ({
  notification: n,
  onDismiss,
  persistent = false,
}: Props) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const route = resolveRoute(n);

  // Trigger slide-in after first paint
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setTimeout(onDismiss, 320);
  };

  // Auto-dismiss + progress bar
  useEffect(() => {
    if (persistent) return;
    const start = Date.now();

    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - start) / DISMISS_MS) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    timerRef.current = setTimeout(dismiss, DISMISS_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { icon, cls } = getIcon(n.category);
  const hasActor = Boolean(n.actor && ACTOR_AVATAR_CATEGORIES.has(n.category));
  const actorName = n.actor ? `${n.actor.firstName} ${n.actor.lastName}` : "";
  const headerLabel = hasActor
    ? actorName
    : (CATEGORY_LABEL[n.category as NotificationCategory] ?? "Notification");


  return (
    <div
      style={{
        transform: visible
          ? "translateX(0)"
          : "translateX(calc(100% + 24px))",
        transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        width: 320,
      }}
    >
      <Toast
        isOpen
        style={{
          width: "100%",
          maxWidth: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
          borderRadius: 10,
          overflow: "hidden",
          cursor: route ? "pointer" : "default",
        }}
        onClick={() => {
          if (route) { navigate(route); dismiss(); }
        }}
      >
        <ToastHeader
          toggle={(e) => { (e as React.MouseEvent).stopPropagation(); dismiss(); }}
        >
          <span
            className={`rounded-circle d-inline-flex align-items-center justify-content-center me-2 flex-shrink-0 ${cls}`}
            style={{ width: 28, height: 28, fontSize: 14 }}
          >
            <i className={icon} />
          </span>
          <span className="fw-semibold" style={{ fontSize: "0.8125rem" }}>
            {headerLabel}
          </span>
        </ToastHeader>

        <ToastBody
          style={{ paddingTop: 8, paddingBottom: progress < 100 || persistent ? 10 : 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="fw-semibold text-body" style={{ fontSize: "0.8125rem", lineHeight: 1.3 }}>
            {n.title}
          </div>
          {n.message ? (
            <div
              className="text-muted mt-1"
              style={{
                fontSize: "0.75rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                WebkitBoxOrient: "vertical" as any,
                overflow: "hidden",
                lineHeight: 1.4,
              }}
            >
              {n.message}
            </div>
          ) : null}
          <div className="text-muted mt-2" style={{ fontSize: "0.7rem" }}>
            <i className="mdi mdi-clock-outline me-1" />
            {moment(n.created_at).fromNow()}
          </div>
        </ToastBody>

        {/* Progress bar */}
        {!persistent && (
          <div style={{ height: 3, background: "var(--bs-border-color)" }}>
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: "var(--bs-primary)",
                transition: "width 0.1s linear",
              }}
            />
          </div>
        )}
      </Toast>
    </div>
  );
};

export default NotificationPreviewToast;
